import ConfigurableList from 'scoreboard/components/configurable-list'
import AndOrLinkControls, { AND, OR } from './and-or-link-controls'
import _ from 'lodash'
import StatementType from './model/statement-type'
const { AND_OR, OR_AND } = StatementType
import deleteListItemAction from './delete-list-item-action.hbs'
import grip from './grip.hbs'
import formHelper from 'scoreboard/components/form-helper'
import FormView from 'scoreboard/components/form-view'
import Backbone from 'backbone'
import nextLinkGroup, { MIN_LINK_GROUP } from 'scoreboard/datasets/filters/next-link-group'
import {missingConditionsOrActionsError} from '../../forms/edit/form-logician'

const ConjunctionEditor = ConfigurableList.extend(/** @lends {ConjunctionEditor} */{

	className: 'conjunction-editor ' + ConfigurableList.prototype.className,

	/**
	 * @type {Conjunction} the current state of the conjunction shown in the
	 * view
	 */
	conjunction: undefined,

	/**
	 * Used to build a Conjunction, such as used with dataset filters (#46588).
	 * A conjunction is a list of conditions without any explicit consequences
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Conjunction} [options.conjunction] an existing conjunction to be
	 * edited
	 *
	 * @param {Function} options.conditionEditorCreator a Function that returns
	 * a new instance of an editor for a Condition. The Condition may be new or
	 * existing
	 *
	 * @param {FormView} options.formView the FormView that this
	 * ConjunctionEditor is contained within
	 */
	initialize: function(options = {}) {
		ConfigurableList.prototype.initialize.apply(this, arguments)
		this.formView = options.formView ?? new FormView({
			el: this.el
		})
		this.conjunction = _.cloneDeep(options.conjunction) || {
			statementType: AND_OR
		}
		this.conjunction.conditions = this.conjunction?.conditions ??
			// if no conditions specified, start with an empty condition
			[{ linkGroup: MIN_LINK_GROUP }]
		this.addLabel = options.addLabel || 'logic.addCondition'
		this.allowLastItemDeletion = options.allowLastItemDeletion ?? true
		this.conditionEditors = []
		this.andOrLinkControls = []
		this.on('move', () => {
			this.renumberLinkGroups()
			this.formView?.change()
		})
	},

	render: function() {
		_.invokeMap(this.conditionEditors, 'remove')
		ConfigurableList.prototype.render.apply(this, arguments)
		// if no FormView was passed in, go ahead and start the one we created
		// in the initialize method
		if (!this.options.formView) {
			this.formView.start()
		}
	},

	renderItems: function() {
		this.draggableListEl = this.$('.draggable-list').empty()
		let conditionsByLinkGroup = _.groupBy(this.conjunction.conditions, 'linkGroup')
		let itemEl
		Object.keys(conditionsByLinkGroup).forEach((linkGroup, i) => {
			let conditions = conditionsByLinkGroup[linkGroup]
			this.draggableListEl.append('<li class="item"></li>')
			itemEl = this.draggableListEl.find('> .item').last()
			this.renderLinkGroup(itemEl, conditions)
			// include and/or toggle and link/unlink button between link groups
			if (i < _.size(conditionsByLinkGroup) - 1) {
				this.draggableListEl.append('<li class="item draggable-list-lock"></li>')
				itemEl = this.draggableListEl.find('.item').last()
				let andOr = this.conjunction.statementType == OR_AND ? OR : AND
				this.renderAndOrLinkControls(itemEl, { andOr })
			}
		})
	},

	/**
	 * Renders all the conditions within a single link group. Includes the
	 * and/or toggle and link/unlink button between conditions. Also includes
	 * buttons to delete each of the conditions, and a
	 * @param {jQuery} itemEl the item element to render the link group into
	 * @param {Condition[]} conditions
	 */
	renderLinkGroup: function(itemEl, conditions) {
		itemEl.attr('data-link-group', conditions[0].linkGroup)
		let statementType = this.readFromDom().statementType
		let andOr = statementType == OR_AND ? AND : OR
		conditions.forEach((condition, index) => {
			let view = this.options.conditionEditorCreator?.(condition, index)
			this.conditionEditors.push(view)
			itemEl.append(`<div class="condition-editor-wrapper">`)
			let conditionEl = itemEl.find('.condition-editor-wrapper').last()
			conditionEl
				.append(view.el, deleteListItemAction())
				.data('view', view)
			view.render()
			if (index < _.size(conditions) - 1) {
				let state = { andOr, linked: true }
				this.renderAndOrLinkControls(itemEl, state)
			}
		})
		itemEl.append(grip())
	},

	/**
	 * @param [state] the initial state of the AndOrLinkControls
	 *
	 * @param {String} [state.andOr] either 'and' or 'or'
	 *
	 * @param {Boolean} [state.linked=false] indicates whether the conditions
	 * are currently linked. When linked, the button will be labeled "Unlink",
	 * and when the not linked, the button will be labeled "Link"
	 */
	renderAndOrLinkControls(itemEl, state) {
		let controls = new AndOrLinkControls({ state })
		this.andOrLinkControls.push(controls)
		itemEl.append(controls.el)
		controls.render()
		// must be after adding to this.andOrLinkControls
		controls.on('change:andOr', this.toggleStatementType.bind(this))
		controls.on('change:linked', this.toggleLinked.bind(this))
	},

	addItem: function() {
		let fromDom = this.readFromDom()

		// add andOrLinkControls
		if (!_.isEmpty(fromDom.conditions)) {
			this.draggableListEl.append('<li class="item draggable-list-lock"></li>')
			let itemEl = this.draggableListEl.find('.item').last()
			let andOr = fromDom.statementType == OR_AND ? OR : AND
			this.renderAndOrLinkControls(itemEl, {andOr})
		}

		// add a condition
		this.draggableListEl.append('<li class="item"></li>')
		let itemEl = this.draggableListEl.find('> .item').last()
		let conditions = fromDom.conditions
		let condition = { linkGroup: nextLinkGroup(conditions) }
		this.renderLinkGroup(itemEl, [ condition ])
		this.formView?.change()
	},

	isInsideLinkGroup: function(el) {
		return this.$(el).closest('[data-link-group]').length > 0
	},

	/**
	 * Whether the given element is in a top level condition, as opposed to
	 * being inside a larger link group with more than one condition
	 * @param el
	 */
	isInTopLevelCondition: function(el) {
		return !this.$(el).closest('.draggable-list-lock').length > 0
	},

	/**
	 * Toggles the statement type between AND and OR
	 * @param andOr the new value for the toggle
	 * @param el the element for the toggle that was clicked on
	 */
	toggleStatementType: function(andOr, el) {
		let outsideAndOr = andOr
		let insideAndOr = oppositeAndOr(andOr)
		if (this.isInsideLinkGroup(el)) {
			outsideAndOr = oppositeAndOr(outsideAndOr)
			insideAndOr = oppositeAndOr(insideAndOr)
		}

		// let oppositeAndOr = andOr == AND ? OR : AND
		_.each(this.andOrLinkControls, controls => {
			if (this.isInsideLinkGroup(controls.el)) {
				controls.setAndOr(insideAndOr)
			} else {
				controls.setAndOr(outsideAndOr)
			}
		})
		this.formView?.change()
	},

	/**
	 * Links or unlinks conditions within a link group.
	 * @param linked whether the conditions are now linked or unlinked
	 * @param el the element for the button that was clicked on
	 */
	toggleLinked: function(linked, el) {
		let fromDom = this.readFromDom()
		if (linked) {
			this.link(el, fromDom)
		} else {
			this.unlink(el, fromDom)
		}
		this.formView?.change()
	},

	unlink: function(el, fromDom) {
		// update the linkGroup numbers for the link groups after the ones
		// being unlinked
		let subsequentLinkGroupEls = this.$(el)
			.closest('[data-link-group]')
			.nextAll()
			.filter('[data-link-group]')
		subsequentLinkGroupEls.each((index, el) => {
			let linkGroup = parseInt(this.$(el).attr('data-link-group'))
			this.$(el).attr('data-link-group', linkGroup + 1)
		})

		// break up the existing conditions into two new link groups
		let linkGroupToSplit = parseInt(this.$(el)
			.closest('[data-link-group]')
			.attr('data-link-group'))
		let conditionsToSplit = _.filter(fromDom.conditions, condition => {
			return condition.linkGroup == linkGroupToSplit
		})
		let linkGroupCutoff = this.$(el)
			.closest('.and-or-link-controls')
			.prevAll()
			.filter('.condition-editor-wrapper')
			.length
		let firstLinkGroup = _.slice(conditionsToSplit, 0, linkGroupCutoff)
		let secondLinkGroup = _.slice(conditionsToSplit, linkGroupCutoff)
		// increment linkGroup for the second link group
		_.each(secondLinkGroup, condition => {
			condition.linkGroup = parseInt(condition.linkGroup) + 1
		})

		// render the first new link group
		let itemEl = this.$(el).closest('[data-link-group]')
			.before(`<li class="item"></li>`)
		this.renderLinkGroup(itemEl.prev(), firstLinkGroup)

		// render the divider between the first and second link groups
		itemEl.before('<li class="item draggable-list-lock"></li>')
		let andOr = fromDom.statementType == OR_AND ? OR : AND
		this.renderAndOrLinkControls(itemEl.prev(), { andOr })

		// render the second new link group
		this.$(el).closest('[data-link-group]')
			.before(`<li class="item"></li>`)
		this.renderLinkGroup(itemEl.prev(), secondLinkGroup)

		// remove the old link group
		this.$(el).closest('[data-link-group]').remove()

		// #52605 make sure the draggable list still works
		this.renderDraggableList()
	},

	link: function(el, fromDom) {
		// find the parent item this is a part of
		let itemEl = this.$(el).closest('.item')
		let prevLinkGroup = parseInt(itemEl.prev().attr('data-link-group'))
		let nextLinkGroup = parseInt(itemEl.next().attr('data-link-group'))

		// update the linkGroup numbers
		_.each(fromDom.conditions, condition => {
			let linkGroup = parseInt(condition.linkGroup)
			if (linkGroup == nextLinkGroup) {
				condition.linkGroup = prevLinkGroup
			}
		})

		// render the newly combined link group
		let linkGroupConditions = _.filter(fromDom.conditions, condition => {
			return condition.linkGroup == prevLinkGroup
		})

		// #53154 remove the marked fields from the item before reusing it
		_.invokeMap(formHelper.getMarkedFields(itemEl.prev()), 'remove')
		this.renderLinkGroup(itemEl.prev().empty(), linkGroupConditions)

		// remove old junk
		itemEl.next().remove()
		this.$(el).closest('.item.draggable-list-lock').remove()

		// update data-link-group for items after nextLinkGroup
		this.renumberLinkGroups()

		// #53033 make sure if there's now only one item that it's not draggable
		this.renderDraggableList()
	},

	onClickDeleteListItem: function(e) {
		// clear any error tooltips
		// #52882 clear errors before removing the marked field so we don't
		// have a lingering tooltip
		this.formView?.clearErrors()
		let fromDom = this.readFromDom()
		let linkGroupCounts = _.countBy(fromDom.conditions, 'linkGroup')
		let linkGroup = parseInt(this.$(e.target)
			.closest('[data-link-group]')
			.attr('data-link-group'))

		// if this is in a top level group
		if (linkGroupCounts[linkGroup] == 1) {
			let itemEl = this.$(e.target).closest('.item')
			let andOrLinkControlsEl = linkGroup == MIN_LINK_GROUP ? itemEl.next() : itemEl.prev()
			itemEl.remove()
			andOrLinkControlsEl.remove()

			// make sure linkGroups are correctly updated
			this.renumberLinkGroups()
		} else { // we're deleting something inside a link group
			let conditionEl = this.$(e.target).closest('.condition-editor-wrapper')

			// find the andOrLinkControlsEl to remove
			let andOrLinkControlsEl
			// if this is the first condition
			if (conditionEl.prev().length == 0) {
				andOrLinkControlsEl = conditionEl.next()
			} else {
				andOrLinkControlsEl = conditionEl.prev()
			}
			andOrLinkControlsEl.remove()

			conditionEl.remove()
		}

		// #52958 make sure if there's only one condition that it isn't draggable
		this.renderDraggableList()

		this.formView?.change()
	},

	/**
	 * Ensures the linkGroups are numbered correctly. Useful after doing a
	 * drag and drop operation
	 */
	renumberLinkGroups: function() {
		// for draggable lists, there is a "traveler" item created when dragging
		// an item. We want to make sure to not include when grabbing the items
		// to renumber
		_.each(this.$('[data-link-group]:not(.traveler)'), (itemEl, linkGroup) => {
			this.$(itemEl).attr('data-link-group', linkGroup)
		})
	},

	readFromDom: function() {
		// read the statementType
		let statementType
		if (_.isEmpty(this.andOrLinkControls)) {
			statementType = this.conjunction.statementType
		} else {
			let andOr = this.andOrLinkControls[0].andOrToggle.val()
			if (this.isInsideLinkGroup(this.andOrLinkControls[0].el)) {
				andOr = oppositeAndOr(andOr)
			}
			statementType = andOr == AND ? AND_OR : OR_AND
		}

		// read the conditions
		let selector = '.condition-editor-wrapper'
		let conditions = _.map(this.draggableListEl.find(selector), wrapperEl => {
			wrapperEl = this.$(wrapperEl)
			let condition = wrapperEl.data('view')?.readFromDom?.() ??
				formHelper.readFromDom(wrapperEl)
			condition.linkGroup = parseInt(wrapperEl
				.closest('[data-link-group]')
				.attr('data-link-group'))
			return condition
		})

		return {
			statementType,
			conditions
		}
	},

	test: function() {
		// if the conjunction editor is present, this is an if or if else block
		// so it needs to have conditions
		if (_.size(this.readFromDom()?.conditions) == 0) {
			return missingConditionsOrActionsError()
		}

		for (let i = 0; i < this.conditionEditors?.length ?? 0; i++) {
			let test = this.conditionEditors[i]?.test?.()
			if (test) return test
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([...this.andOrLinkControls, ...this.conditionEditors],
			'remove')

		// don't remove the form view unless it was created in this view
		if (!this.options.formView) this.formView.remove()
	},

	autoRemove: false

})

function oppositeAndOr(andOr) {
	return andOr == AND ? OR : AND
}

export default ConjunctionEditor
export { oppositeAndOr, MIN_LINK_GROUP }

ConjunctionEditor.__filename__ = 'components/logic/conjunction-editor.js'
